import { useCallback, useEffect, useMemo, useState } from 'react';
import { api, errorText } from '../api';
import { ROLE_LABEL } from '../auth';
import { Card, Loading, Notice } from '../components/ui';
import { NAV } from '../components/Layout';
import type { Role } from '../types';

interface Bereich {
  schluessel: string;
  label: string;
}

interface RollenAktion {
  schluessel: string;
  faehig: boolean;
  erlaubt: boolean;
}

interface Rollenzeile {
  role: string;
  label: string;
  basis: Role;
  custom: boolean;
  faehigkeit: string[];
  erlaubt: string[];
  versteckt: string[];
  aktionen: RollenAktion[];
}

interface AktionSpalte {
  schluessel: string;
  label: string;
  hinweis: string;
}

interface Matrix {
  bereiche: Bereich[];
  aktionen: AktionSpalte[];
  vorlagen: Role[];
  rollen: Rollenzeile[];
}

/** Anzeigename: eingebaute Rolle über ROLE_LABEL, eigene Rolle über ihr Label. */
function rollenLabel(rollen: Rollenzeile[] | undefined, role: string): string {
  const r = rollen?.find((x) => x.role === role);
  return ROLE_LABEL[role as Role] ?? r?.label ?? role;
}

/** Menüpunkte, die für eine Rolle grundsätzlich in Frage kommen: ein Ziel, der
 *  Bereich ist der Rolle erlaubt, und die fest hinterlegte Rollenliste passt. */
function itemsFuerRolle(role: string, erlaubt: string[]): { to: string; label: string; gruppe: string }[] {
  const out: { to: string; label: string; gruppe: string }[] = [];
  for (const g of NAV) {
    for (const it of g.items) {
      if (!it.to) continue;
      const b = it.bereich ?? g.bereich;
      if (!erlaubt.includes(b)) continue;
      if (it.roles && !it.roles.includes(role as Role)) continue;
      out.push({ to: it.to, label: it.label, gruppe: g.title });
    }
  }
  return out;
}

export function RolesPage() {
  const [matrix, setMatrix] = useState<Matrix | null>(null);
  const [auswahl, setAuswahl] = useState<Record<string, Set<string>>>({});
  // Zusatzrechte je Rolle (Aktionsschlüssel) - im selben Zeilen-Speichern wie die Bereiche.
  const [aktAuswahl, setAktAuswahl] = useState<Record<string, Set<string>>>({});
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState('');

  // Feinsteuerung Menüpunkte
  const [menuRole, setMenuRole] = useState<string>('');
  const [sichtbar, setSichtbar] = useState<Record<string, Set<string>>>({});
  const [menuBusy, setMenuBusy] = useState(false);

  // Eigene Rolle anlegen
  const [neuLabel, setNeuLabel] = useState('');
  const [neuBasis, setNeuBasis] = useState('');
  const [neuBusy, setNeuBusy] = useState(false);

  // Auswahl-/Sichtbarkeits-Sets aus den Rollenzeilen aufbauen (nach Laden/Speichern).
  const fuelleAuswahl = useCallback((rollen: Rollenzeile[]) => {
    const ausw: Record<string, Set<string>> = {};
    const akt: Record<string, Set<string>> = {};
    const sicht: Record<string, Set<string>> = {};
    for (const r of rollen) {
      ausw[r.role] = new Set(r.erlaubt);
      akt[r.role] = new Set(r.aktionen.filter((a) => a.erlaubt).map((a) => a.schluessel));
      const anwendbar = itemsFuerRolle(r.basis, r.erlaubt).map((i) => i.to);
      const versteckt = new Set(r.versteckt);
      sicht[r.role] = new Set(anwendbar.filter((to) => !versteckt.has(to)));
    }
    setAuswahl(ausw);
    setAktAuswahl(akt);
    setSichtbar(sicht);
  }, []);

  const laden = useCallback(async () => {
    setError('');
    try {
      const m = await api<Matrix>('/api/rollen');
      setMatrix(m);
      fuelleAuswahl(m.rollen);
      setMenuRole((cur) => cur || m.rollen[0]?.role || '');
    } catch (err) {
      setError(errorText(err));
    }
  }, [fuelleAuswahl]);

  useEffect(() => {
    void laden();
  }, [laden]);

  function umschalten(role: string, bereich: string) {
    setAuswahl((alt) => {
      const menge = new Set(alt[role]);
      if (menge.has(bereich)) menge.delete(bereich);
      else menge.add(bereich);
      return { ...alt, [role]: menge };
    });
  }

  function aktionUmschalten(role: string, aktion: string) {
    setAktAuswahl((alt) => {
      const menge = new Set(alt[role]);
      if (menge.has(aktion)) menge.delete(aktion);
      else menge.add(aktion);
      return { ...alt, [role]: menge };
    });
  }

  async function speichern(role: string) {
    setBusy(role);
    setError('');
    setMessage('');
    try {
      // Erst die Bereiche, dann jede geänderte Aktion - beides aus derselben Zeile.
      let rollen = (
        await api<{ rollen: Rollenzeile[] }>('/api/rollen', {
          method: 'PUT',
          body: { role, bereiche: [...(auswahl[role] ?? new Set())] },
        })
      ).rollen;
      const zeile = matrix?.rollen.find((r) => r.role === role);
      const aktJetzt = aktAuswahl[role] ?? new Set<string>();
      for (const a of zeile?.aktionen ?? []) {
        if (!a.faehig) continue;
        const sollErlaubt = aktJetzt.has(a.schluessel);
        if (sollErlaubt !== a.erlaubt) {
          rollen = (
            await api<{ rollen: Rollenzeile[] }>('/api/rollen/aktion', {
              method: 'PUT',
              body: { role, aktion: a.schluessel, erlaubt: sollErlaubt },
            })
          ).rollen;
        }
      }
      setMessage(`Rechte für „${rollenLabel(rollen, role)}" gespeichert.`);
      setMatrix((vorher) => (vorher ? { ...vorher, rollen } : vorher));
      fuelleAuswahl(rollen);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy('');
    }
  }

  async function rolleAnlegen() {
    if (neuLabel.trim().length < 2 || !neuBasis) return;
    setNeuBusy(true);
    setError('');
    setMessage('');
    try {
      const res = await api<{ rolle: { label: string }; rollen: Rollenzeile[] }>('/api/rollen/rolle', {
        method: 'POST',
        body: { label: neuLabel.trim(), basis: neuBasis },
      });
      setMessage(`Rolle „${res.rolle.label}" angelegt.`);
      setNeuLabel('');
      setNeuBasis('');
      setMatrix((v) => (v ? { ...v, rollen: res.rollen } : v));
      fuelleAuswahl(res.rollen);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setNeuBusy(false);
    }
  }

  async function rolleLoeschen(role: string, label: string) {
    if (!window.confirm(`Rolle „${label}" wirklich löschen? Ihre Rechte-Einstellungen gehen verloren.`)) return;
    setError('');
    setMessage('');
    try {
      const res = await api<{ rollen: Rollenzeile[] }>(`/api/rollen/rolle/${encodeURIComponent(role)}`, {
        method: 'DELETE',
      });
      setMessage(`Rolle „${label}" gelöscht.`);
      if (menuRole === role) setMenuRole(res.rollen[0]?.role ?? '');
      setMatrix((v) => (v ? { ...v, rollen: res.rollen } : v));
      fuelleAuswahl(res.rollen);
    } catch (err) {
      setError(errorText(err));
    }
  }

  function geaendert(r: Rollenzeile): boolean {
    const jetzt = auswahl[r.role] ?? new Set<string>();
    if (jetzt.size !== r.erlaubt.length || r.erlaubt.some((b) => !jetzt.has(b))) return true;
    const aktJetzt = aktAuswahl[r.role] ?? new Set<string>();
    const aktErlaubt = r.aktionen.filter((a) => a.erlaubt).map((a) => a.schluessel);
    if (aktJetzt.size !== aktErlaubt.length) return true;
    return aktErlaubt.some((a) => !aktJetzt.has(a));
  }

  // --- Menüpunkt-Feinsteuerung ---
  const aktuelleRolle = matrix?.rollen.find((r) => r.role === menuRole);
  const menuItems = useMemo(
    () => (aktuelleRolle ? itemsFuerRolle(aktuelleRolle.basis, aktuelleRolle.erlaubt) : []),
    [aktuelleRolle],
  );
  const menuGruppen = useMemo(() => {
    const map = new Map<string, { to: string; label: string }[]>();
    for (const it of menuItems) {
      if (!map.has(it.gruppe)) map.set(it.gruppe, []);
      map.get(it.gruppe)!.push({ to: it.to, label: it.label });
    }
    return [...map.entries()];
  }, [menuItems]);

  function punktUmschalten(role: string, to: string) {
    setSichtbar((alt) => {
      const menge = new Set(alt[role]);
      if (menge.has(to)) menge.delete(to);
      else menge.add(to);
      return { ...alt, [role]: menge };
    });
  }

  function menuGeaendert(): boolean {
    if (!aktuelleRolle) return false;
    const anwendbar = menuItems.map((i) => i.to);
    const sichtJetzt = sichtbar[aktuelleRolle.role] ?? new Set<string>();
    const verstecktJetzt = anwendbar.filter((to) => !sichtJetzt.has(to)).sort();
    const verstecktGespeichert = [...aktuelleRolle.versteckt].filter((to) => anwendbar.includes(to)).sort();
    return (
      verstecktJetzt.length !== verstecktGespeichert.length ||
      verstecktJetzt.some((to, i) => to !== verstecktGespeichert[i])
    );
  }

  async function menuSpeichern() {
    if (!aktuelleRolle) return;
    setMenuBusy(true);
    setError('');
    setMessage('');
    try {
      const anwendbar = menuItems.map((i) => i.to);
      const sichtJetzt = sichtbar[aktuelleRolle.role] ?? new Set<string>();
      const versteckt = anwendbar.filter((to) => !sichtJetzt.has(to));
      const m = await api<{ rollen: Rollenzeile[] }>('/api/rollen/menu', {
        method: 'PUT',
        body: { role: aktuelleRolle.role, versteckt },
      });
      setMessage(`Menüpunkte für „${rollenLabel(matrix?.rollen, aktuelleRolle.role)}" gespeichert.`);
      setMatrix((vorher) => (vorher ? { ...vorher, rollen: m.rollen } : vorher));
    } catch (err) {
      setError(errorText(err));
    } finally {
      setMenuBusy(false);
    }
  }

  if (!matrix) return error ? <Notice kind="error">{error}</Notice> : <Loading />;

  return (
    <>
      {error && <Notice kind="error">{error}</Notice>}
      {message && <Notice kind="success">{message}</Notice>}

      <Card title="Rollen & Rechte">
        <p className="small muted" style={{ marginTop: 0 }}>
          Links je <strong>Bereich</strong>, rechts (abgesetzt) einzelne <strong>Zusatzrechte</strong>
          {' '}wie „Artikel anlegen & bearbeiten"; darunter fein je <strong>Menüpunkt</strong>. Rechte
          lassen sich einer Rolle entziehen — nie über ihre vorgesehene Fähigkeit hinaus geben
          (ausgegraute Felder). Änderungen je Zeile mit <strong>Speichern</strong> übernehmen; die
          Sperre gilt serverseitig.
        </p>
        <p className="small muted">
          Nicht änderbar: <strong>Administrator</strong> (Vollzugriff), <strong>Betriebsprüfung</strong>{' '}
          (nur lesend, § 147 AO) und <strong>Auftraggeber</strong> (sieht nur die eigene Ware).
        </p>
      </Card>

      <Card padded={false}>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Rolle</th>
                {matrix.bereiche.map((b) => (
                  <th key={b.schluessel} style={{ textAlign: 'center' }}>
                    {b.label}
                  </th>
                ))}
                {matrix.aktionen.map((a, i) => (
                  <th
                    key={a.schluessel}
                    style={{ textAlign: 'center', borderLeft: i === 0 ? '2px solid var(--line)' : undefined }}
                    title={a.hinweis}
                  >
                    {a.label}
                  </th>
                ))}
                <th />
              </tr>
            </thead>
            <tbody>
              {matrix.rollen.map((r) => (
                <tr key={r.role}>
                  <td style={{ fontWeight: 600 }}>
                    {rollenLabel(matrix.rollen, r.role)}
                    {r.custom && (
                      <div className="small muted" style={{ fontWeight: 400 }}>
                        eigene Rolle · Vorlage {rollenLabel(matrix.rollen, r.basis)}
                      </div>
                    )}
                  </td>
                  {matrix.bereiche.map((b) => {
                    const kann = r.faehigkeit.includes(b.schluessel);
                    const an = auswahl[r.role]?.has(b.schluessel) ?? false;
                    return (
                      <td key={b.schluessel} style={{ textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={kann && an}
                          disabled={!kann}
                          title={kann ? '' : 'Für diese Rolle nicht vorgesehen'}
                          onChange={() => umschalten(r.role, b.schluessel)}
                        />
                      </td>
                    );
                  })}
                  {matrix.aktionen.map((a, i) => {
                    const zelle = r.aktionen.find((x) => x.schluessel === a.schluessel);
                    const kann = zelle?.faehig ?? false;
                    const an = aktAuswahl[r.role]?.has(a.schluessel) ?? false;
                    return (
                      <td
                        key={a.schluessel}
                        style={{ textAlign: 'center', borderLeft: i === 0 ? '2px solid var(--line)' : undefined }}
                      >
                        <input
                          type="checkbox"
                          checked={kann && an}
                          disabled={!kann}
                          title={kann ? '' : 'Für diese Rolle nicht vorgesehen'}
                          onChange={() => aktionUmschalten(r.role, a.schluessel)}
                        />
                      </td>
                    );
                  })}
                  <td className="num" style={{ whiteSpace: 'nowrap' }}>
                    <button
                      className="small primary"
                      disabled={busy === r.role || !geaendert(r)}
                      onClick={() => speichern(r.role)}
                    >
                      {busy === r.role ? '…' : 'Speichern'}
                    </button>
                    {r.custom && (
                      <button
                        className="small"
                        style={{ marginLeft: 6, color: 'var(--bad)', borderColor: 'var(--bad-line)' }}
                        title="Eigene Rolle löschen"
                        onClick={() => void rolleLoeschen(r.role, rollenLabel(matrix.rollen, r.role))}
                      >
                        Löschen
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Card title="Eigene Rolle anlegen">
        <p className="small muted" style={{ marginTop: 0 }}>
          Eine eigene Rolle baut auf einer <strong>Vorlage</strong> auf. Die Vorlage bestimmt die
          Obergrenze und die fest verdrahteten Rechte (Buchen, Löschen …); anschließend justierst du
          sie oben in der Matrix wie jede andere Rolle. Löschen ist nur möglich, solange ihr kein
          Benutzer zugewiesen ist.
        </p>
        <div className="toolbar" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <label className="small" style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 220 }}>
            Name der Rolle
            <input value={neuLabel} onChange={(e) => setNeuLabel(e.target.value)} placeholder="z. B. Teamleitung Lager" maxLength={60} />
          </label>
          <label className="small" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            Vorlage
            <select value={neuBasis} onChange={(e) => setNeuBasis(e.target.value)}>
              <option value="">— wählen —</option>
              {matrix.vorlagen.map((v) => (
                <option key={v} value={v}>
                  {rollenLabel(matrix.rollen, v)}
                </option>
              ))}
            </select>
          </label>
          <button
            className="small primary"
            disabled={neuBusy || neuLabel.trim().length < 2 || !neuBasis}
            onClick={() => void rolleAnlegen()}
          >
            {neuBusy ? '…' : 'Rolle anlegen'}
          </button>
        </div>
      </Card>

      <Card title="Einzelne Menüpunkte je Rolle">
        <p className="small muted" style={{ marginTop: 0 }}>
          Innerhalb der erlaubten Bereiche lässt sich jeder Menüpunkt für eine Rolle abwählen. Ein
          abgewählter Punkt verschwindet aus dem Menü dieser Rolle und ist in der Oberfläche nicht mehr
          erreichbar.
        </p>
        <div className="toolbar" style={{ marginBottom: 12 }}>
          <label className="small" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            Rolle
            <select value={menuRole} onChange={(e) => setMenuRole(e.target.value)}>
              {matrix.rollen.map((r) => (
                <option key={r.role} value={r.role}>
                  {rollenLabel(matrix.rollen, r.role)}
                </option>
              ))}
            </select>
          </label>
          <span className="spacer" style={{ flex: 1 }} />
          <button className="small primary" disabled={menuBusy || !menuGeaendert()} onClick={() => void menuSpeichern()}>
            {menuBusy ? '…' : 'Speichern'}
          </button>
        </div>

        {menuGruppen.length === 0 ? (
          <p className="small muted">Für diese Rolle sind keine Bereiche freigegeben.</p>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16 }}>
            {menuGruppen.map(([gruppe, items]) => (
              <div key={gruppe}>
                <div className="nav-group" style={{ paddingLeft: 0 }}>{gruppe}</div>
                {items.map((it) => {
                  const an = aktuelleRolle ? sichtbar[aktuelleRolle.role]?.has(it.to) ?? false : false;
                  return (
                    <label key={it.to} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0' }}>
                      <input
                        type="checkbox"
                        checked={an}
                        onChange={() => aktuelleRolle && punktUmschalten(aktuelleRolle.role, it.to)}
                      />
                      <span className="small">{it.label}</span>
                    </label>
                  );
                })}
              </div>
            ))}
          </div>
        )}
      </Card>
    </>
  );
}
